import { getDecoded } from '@/lib/auth';
import { getPouchDB } from '@/lib/couch';

export interface BaseDoc {
  _id?: string;
  _rev?: string;
  type: string;
  tenant_id?: string;
  // epoch-ms write stamp; drives last-write-wins conflict resolution
  updatedAt?: number;
}

function newId(type: string): string {
  return `${type}:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function allByType<T extends BaseDoc>(type: string): Promise<T[]> {
  const db = getPouchDB();
  const res = await db.allDocs<T>({
    include_docs: true,
    startkey: `${type}:`,
    endkey: `${type}:￰`,
  });
  return res.rows
    .map(r => r.doc as T | undefined)
    .filter((d): d is T => Boolean(d) && d!.type === type);
}

export async function put<T extends BaseDoc>(doc: T): Promise<T> {
  const db = getPouchDB();
  const decoded = getDecoded();
  const tenantId = doc.tenant_id ?? decoded?.tenant_id;
  const next: T = {
    ...doc,
    _id: doc._id ?? newId(doc.type),
    tenant_id: tenantId,
    updatedAt: Date.now(),
  };
  // Domain shadow-puts (apps/frontend/src/store/index.ts) do not track _rev
  // in the zustand state. To make repeat writes to the same _id idempotent
  // (e.g. add extinguisher → update customer doc), fetch the existing rev
  // and retry once on a 409 conflict.
  try {
    const res = await db.put(next);
    return { ...next, _rev: res.rev };
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status !== 409 || !next._id) throw err;
    try {
      const existing = await db.get(next._id);
      const withRev: T = { ...next, _rev: existing._rev };
      const res = await db.put(withRev);
      return { ...withRev, _rev: res.rev };
    } catch (innerErr) {
      throw innerErr;
    }
  }
}

export async function remove(doc: BaseDoc): Promise<void> {
  const db = getPouchDB();
  if (!doc._id || !doc._rev) return;
  await db.remove(doc._id, doc._rev);
}

// Delete a doc by its `<type>:<idPart>` _id. The zustand state does not track
// _rev, so fetch the live doc for its rev before removing. A missing doc (404)
// is treated as success — the delete already happened or never replicated.
export async function removeByTypeId(
  type: string,
  idPart: string
): Promise<void> {
  const db = getPouchDB();
  const id = `${type}:${idPart}`;
  try {
    const existing = await db.get(id);
    await db.remove(existing._id, existing._rev);
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404) return;
    throw err;
  }
}

export type ChangeHandler = (
  change: PouchDB.Core.ChangesResponseChange<BaseDoc>
) => void;

export function changes(types: string[], onChange: ChangeHandler): () => void {
  const db = getPouchDB();
  const feed = db
    .changes<BaseDoc>({ since: 'now', live: true, include_docs: true })
    .on('change', c => {
      const t = c.doc?.type;
      if (!t || types.includes(t)) onChange(c);
    });
  return () => {
    try {
      feed.cancel();
    } catch {
      // ignore
    }
  };
}
